/**
 * Firestore query builders exported by `@principle-theorem/shared` that
 * `MockSharedFirestoreQueries` can replace with Jest mocks.
 */
export type SharedFirestoreQuery =
  | 'collectionGroupQuery'
  | 'toQuery'
  | 'undeletedQuery'
  | 'where';

/**
 * Stand-in for the constraint the real `where` returns. That constraint carries
 * an opaque `_apply` closure which two otherwise identical constraints never
 * share, so it can never be compared with `toEqual`. Recording the arguments
 * instead lets a spec assert on the constraints a query was built from, rather
 * than on an indistinguishable `undefined`.
 */
export interface IMockWhereConstraint {
  type: 'where';
  fieldPath: unknown;
  opStr: unknown;
  value: unknown;
}

/**
 * Only ever runs inside a Jest worker, where `jest` is a global. The
 * declaration gives that global a type without importing `@jest/globals`,
 * which throws when evaluated outside Jest and would break the app bundles
 * that compile this library through `@principle-theorem/testing`.
 */
declare const jest: typeof import('@jest/globals').jest;

/**
 * Builds a mocked `@principle-theorem/shared` module for `jest.mock`, replacing
 * the named Firestore query builders with Jest mocks and passing every other
 * export through to the real module.
 *
 * Query builders are pure functions with no seam, so a spec that wants to
 * assert on the query a model built has to intercept them at the module
 * boundary. Spreading the module also yields a plain writable object, which is
 * what makes `jest.spyOn(shared, 'where')` possible at all — the real module
 * namespace is read-only.
 *
 * Resolve it with `jest.requireActual` inside the factory. A top-level import
 * is not safe: `jest.mock` is hoisted above the spec's imports, so the binding
 * would still be uninitialised when the factory runs.
 *
 * ```ts
 * jest.mock('@principle-theorem/shared', () => {
 *   const testing = jest.requireActual<
 *     typeof import('@principle-theorem/testing')
 *   >('@principle-theorem/testing');
 *   return testing.MockSharedFirestoreQueries(['collectionGroupQuery', 'where']);
 * });
 * ```
 *
 * Keep this module free of runtime imports from `@principle-theorem/shared`.
 * Requiring the barrel from inside the factory would re-enter that same factory
 * if anything it pulls in imports `@principle-theorem/shared`, and on re-entry
 * the barrel is only partially populated.
 */
export function MockSharedFirestoreQueries(
  queries: SharedFirestoreQuery[]
): Record<string, unknown> {
  const actual = jest.requireActual<Record<string, unknown>>(
    '@principle-theorem/shared'
  );
  return {
    ...actual,
    ...(queries.includes('collectionGroupQuery')
      ? { collectionGroupQuery: jest.fn() }
      : {}),
    ...(queries.includes('toQuery') ? { toQuery: jest.fn() } : {}),
    ...(queries.includes('undeletedQuery')
      ? { undeletedQuery: jest.fn() }
      : {}),
    ...(queries.includes('where')
      ? {
          where: jest.fn(
            (
              fieldPath: unknown,
              opStr: unknown,
              value: unknown
            ): IMockWhereConstraint => ({
              type: 'where',
              fieldPath,
              opStr,
              value,
            })
          ),
        }
      : {}),
  };
}
